import React from "react";
import { CheckCircle } from "lucide-react";
import { AVAILABLE_AVATARS, type AvatarConfig } from "../types/avatar";

type AvatarGridVariant = "card" | "compact";

type AvatarGridProps = {
  selectedAvatarId?: string | null;
  onSelect: (avatarId: string) => void;
  avatars?: AvatarConfig[];
  disabled?: boolean;
  gridClassName?: string;
  variant?: AvatarGridVariant;
  selectedHint?: string;
  imageErrorAvatarIds?: Set<string>;
  onImageError?: (avatarId: string) => void;
};

const getContainerClass = (variant: AvatarGridVariant) => {
  switch (variant) {
    case "compact":
      return "grid grid-cols-2 gap-3 sm:grid-cols-3";
    case "card":
    default:
      return "grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-6";
  }
};

const getButtonBaseClass = (variant: AvatarGridVariant) => {
  switch (variant) {
    case "compact":
      return "relative flex flex-col items-center rounded-xl border-2 p-3 transition-all disabled:cursor-not-allowed disabled:opacity-60";
    case "card":
    default:
      return "relative p-3 sm:p-4 rounded-2xl transition-all duration-200 transform hover:scale-105 text-center disabled:cursor-not-allowed disabled:opacity-60";
  }
};

const getSelectedClass = (variant: AvatarGridVariant) => {
  switch (variant) {
    case "compact":
      return "border-blue-500 bg-blue-50 shadow-lg";
    case "card":
    default:
      return "border-2 border-blue-500 bg-blue-50 shadow-lg scale-105";
  }
};

const getDefaultClass = (variant: AvatarGridVariant) => {
  switch (variant) {
    case "compact":
      return "border-gray-200 bg-gray-50 hover:border-blue-400 hover:bg-blue-50";
    case "card":
    default:
      return "border-2 border-gray-300 bg-gray-100 shadow-md hover:shadow-lg";
  }
};

const AvatarGrid: React.FC<AvatarGridProps> = ({
  selectedAvatarId = null,
  onSelect,
  avatars = AVAILABLE_AVATARS,
  disabled = false,
  gridClassName = "",
  variant = "card",
  selectedHint,
  imageErrorAvatarIds,
  onImageError,
}) => {
  const containerClass = `${getContainerClass(variant)} ${gridClassName}`.trim();
  const baseButtonClass = getButtonBaseClass(variant);
  const selectedClass = getSelectedClass(variant);
  const defaultClass = getDefaultClass(variant);

  return (
    <div className={containerClass}>
      {avatars.map((avatar) => {
        const isSelected = selectedAvatarId === avatar.id;
        const buttonClass = `${baseButtonClass} ${isSelected ? selectedClass : defaultClass}`;
        const hasImageError = imageErrorAvatarIds?.has(avatar.id) ?? false;

        return (
          <button
            key={avatar.id}
            type="button"
            onClick={() => onSelect(avatar.id)}
            disabled={disabled}
            className={buttonClass}
          >
            {hasImageError ? (
              <span className="mb-2 flex h-16 w-16 items-center justify-center rounded-full bg-gray-200 text-sm font-semibold text-gray-500 shadow">
                ?
              </span>
            ) : (
              <img
                src={avatar.thumbnail}
                alt={avatar.name}
                className="mb-2 h-16 w-16 rounded-full object-cover shadow"
                onError={onImageError ? () => onImageError(avatar.id) : undefined}
              />
            )}
            <span className="text-xs font-medium text-gray-600">{avatar.name}</span>
            {selectedHint && isSelected ? (
              <p className="mt-1 text-xs font-semibold text-gray-500">{selectedHint}</p>
            ) : null}
            {isSelected ? (
              <span className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-white shadow">
                <CheckCircle className="h-4 w-4" />
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
};

export default AvatarGrid;
